import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// SOLA-56 — Pagina admin view=email: consultazione (sola lettura) di tutte
// le email inviate dal sistema. Da SOLA-60 è raggiungibile anche dal menu
// dell'amministratore (voce "Email"), oltre che via URL (…?view=email).
//
// La pagina non ha nessuna azione di modifica/eliminazione. Il corpo HTML
// viene caricato on-demand (la lista esclude l'html per tenere piccolo il
// payload, vedi SOLA-53) e mostrato in un iframe sandboxato, DOPO aver
// rimosso il tracking pixel: così consultare un'email non falsa lo stato
// di lettura registrato dal pixel né il campo ipAddress del log.

const LIMIT = 100;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// SOLA-59-followup: data evento (YYYY-MM-DD) come dd/mm/yyyy — evita ambiguità
// tra edizioni omonime (es. tre 'Speed Date Lecce' diversi).
const fmtDate = (d) => { const s = String(d || '').slice(0, 10); return s ? s.replace(/-/g, '/') : '—'; };

const TYPE_META = {
  CODES:            { label: 'CODICI',          color: '#6366f1' },
  RESULTS:          { label: 'Risultati',       color: '#db2777' },
  REMINDER:         { label: 'Promemoria',      color: '#3b82f6' },
  EVENT_START:      { label: 'Inizio evento',   color: '#0dcaf0' },
  EVENT_MODIFIED:   { label: 'Evento modificato', color: '#f59e0b' },
  NOTIFICATION:     { label: 'Notifica',        color: '#8b5cf6' },
  CUSTOM:           { label: 'Custom',          color: '#6b7280' },
  TEMPLATE:         { label: 'Template',        color: '#14b8a6' },
  OTP:              { label: 'Codice accesso',  color: '#ea580c' },
};

const typeMeta = (t) => TYPE_META[t] || { label: t || '—', color: '#64748b' };

// SOLA-59 — Stato invio (consegnabilità) dal campo `status` di /admin/emaillogs.
// 'sent' = consegnata, 'pending' = in attesa, 'error' = non consegnabile
// (errore SMTP, evidenziata in rosso).
const SEND_STATUS_META = {
  sent:    { label: 'Inviata',      color: 'success', icon: 'fa-paper-plane' },
  pending: { label: 'In attesa',    color: 'warning', icon: 'fa-clock' },
  error:   { label: 'Errore',       color: 'danger',  icon: 'fa-exclamation-triangle' },
};
const sendStatusMeta = (s) => SEND_STATUS_META[s] || SEND_STATUS_META.pending;

// SOLA-57-followup — esito di consegna al SERVER DI DESTINAZIONE (maillog
// postfix, campo `deliveryStatus` di /admin/emaillogs): 'sent' a livello
// locale non basta, il server remoto può riflutare (spam/blacklist 5xx) o
// rimandare (4xx "slow down").
const DELIVERY_META = {
  delivered: { label: 'Consegnata', color: 'success', icon: 'fa-check-circle' },
  deferred:  { label: 'In coda',    color: 'warning', icon: 'fa-clock' },
  rejected:  { label: 'Rifiutata',  color: 'danger',  icon: 'fa-envelope-exclamation' },
};
const deliveryMeta = (s) => DELIVERY_META[s] || null;

// Rimuove il tracking pixel (e ogni altra immagine di tracciamento) dal
// corpo html prima di mostrarlo nel viewer: consultazione senza side-effect.
const stripTrackingPixels = (html) =>
  String(html || '').replace(/<img[^>]*track-email-read[^>]*>/gi, '');

export default function EmailLogsPage({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [filters, setFilters] = useState({
    emailType: '',
    read: '',
    status: '',
    from: '',
    to: '',
    eventId: '',
  });
  // SOLA-59-followup: elenco eventi per il filtro "Evento" (confronto con la pagina evento)
  const [events, setEvents] = useState([]);

  const [detail, setDetail] = useState(null);   // log aperto nel viewer
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const searchInputRef = useRef(null);

  const fetchLogs = async (pageNum, activeFilters, activeSearch) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(LIMIT));
      params.set('offset', String((pageNum || 0) * LIMIT));
      if (activeFilters.emailType) params.set('emailType', activeFilters.emailType);
      if (activeFilters.read) params.set('read', activeFilters.read);
      if (activeFilters.status) params.set('status', activeFilters.status);
      if (activeFilters.from) params.set('from', activeFilters.from);
      if (activeFilters.to) params.set('to', activeFilters.to);
      if (activeFilters.eventId) params.set('eventId', activeFilters.eventId);
      if (activeSearch) params.set('search', activeSearch);

      const res = await axios.get('/speeddate/api/admin/emaillogs', { params });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reload = (pageNum = 0) => {
    setPage(pageNum);
    fetchLogs(pageNum, filters, appliedSearch);
  };

  useEffect(() => {
    // Filtro "Evento": serve l'elenco eventi (same admin token delle altre API)
    axios.get('/speeddate/api/events')
      .then((res) => setEvents(res.data || []))
      .catch(() => setEvents([]));
    fetchLogs(0, filters, appliedSearch);
  }, []);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setPage(0);
    fetchLogs(0, next, appliedSearch);
  };

  const openDetail = async (log) => {
    setDetail(log);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await axios.get(`/speeddate/api/admin/emaillogs/${log.id}`);
      setDetail(res.data);
    } catch (err) {
      setDetailError(err.response?.data?.error || err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const emailTypes = [...new Set(rows.map((r) => r.emailType))].filter(Boolean).sort();
  const counts = {};
  for (const r of rows) { counts[r.emailType] = (counts[r.emailType] || 0) + 1; }

  const totalPages = Math.max(Math.ceil(total / LIMIT), 1);
  const fromIdx = total === 0 ? 0 : page * LIMIT + 1;
  const toIdx = Math.min((page + 1) * LIMIT, total);

  return (
    <div className="container-fluid">
      <div className="card border-0 shadow-sm glass mb-3">
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h4 className="fw-bold mb-0">
              <i className="fas fa-paper-plane text-primary me-2"></i>Email Inviate
              <span className="badge bg-primary rounded-pill ms-2">{total}</span>
              <small className="text-muted ms-2"><i className="fas fa-lock me-1"></i>Sola lettura</small>
            </h4>
            <div className="d-flex gap-2 align-items-center">
              <select className="form-select form-select-sm" style={{ width: 170 }} value={filters.emailType} onChange={(e) => updateFilter('emailType', e.target.value)}>
                <option value="">Tutti i tipi</option>
                {emailTypes.map((t) => (
                  <option key={t} value={t}>{typeMeta(t).label} ({counts[t] || 0})</option>
                ))}
              </select>
              <select className="form-select form-select-sm" style={{ width: 190 }} value={filters.eventId} onChange={(e) => updateFilter('eventId', e.target.value)} title="Filtra per evento: mostra solo le email dei partecipanti di quell'evento (come nella pagina evento)">
                <option value="">Tutti gli eventi</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name} (#{ev.id})</option>
                ))}
              </select>
              <select className="form-select form-select-sm" style={{ width: 130 }} value={filters.read} onChange={(e) => updateFilter('read', e.target.value)}>
                <option value="">Tutte</option>
                <option value="read">Lette</option>
                <option value="unread">Non lette</option>
              </select>
              <select className="form-select form-select-sm" style={{ width: 140 }} value={filters.status} onChange={(e) => updateFilter('status', e.target.value)} title="EmailStatus — consegnabilità">
                <option value="">Tutte le consegne</option>
                <option value="error">⚠ Con errori</option>
                <option value="sent">Inviate</option>
                <option value="pending">In attesa</option>
              </select>
              <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={filters.from} title="Da data" onChange={(e) => updateFilter('from', e.target.value)} />
              <span className="text-muted small">→</span>
              <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={filters.to} title="A data" onChange={(e) => updateFilter('to', e.target.value)} />
              <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={() => reload(page)} title="Aggiorna">
                <i className="fas fa-sync"></i>
              </button>
            </div>
          </div>

          <div className="d-flex gap-2 align-items-center">
            <input
              ref={searchInputRef}
              type="search"
              className="form-control form-control-sm"
              placeholder="Cerca per destinatario o oggetto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedSearch(search.trim()); setPage(0); fetchLogs(0, filters, search.trim()); } }}
            />
            <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={() => { setAppliedSearch(search.trim()); setPage(0); fetchLogs(0, filters, search.trim()); }}>
              <i className="fas fa-search me-1"></i>Cerca
            </button>
            {appliedSearch && (
              <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => { setSearch(''); setAppliedSearch(''); setPage(0); fetchLogs(0, filters, ''); }}>
                <i className="fas fa-times me-1"></i>Rimuovi filtro
              </button>
            )}
          </div>

          {error && (
            <div className="alert alert-danger py-2 small mt-2">
              <i className="fas fa-exclamation-triangle me-1"></i>{error}
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm glass">
        <div className="card-body p-0">
          {loading && rows.length === 0 ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 small text-uppercase fw-bold text-muted">Data/Ora</th>
                    <th className="small text-uppercase fw-bold text-muted">Destinatario</th>
                    <th className="small text-uppercase fw-bold text-muted">Evento</th>
                    <th className="small text-uppercase fw-bold text-muted">Oggetto</th>
                    <th className="small text-uppercase fw-bold text-muted">Tipo</th>
                    <th className="small text-uppercase fw-bold text-muted text-center" title="Stato invio — consegnabilità (rosso = non consegnabile)">Invio</th>
                    <th className="small text-uppercase fw-bold text-muted">Stato</th>
                    <th className="small text-uppercase fw-bold text-muted">IP lettura</th>
                    <th className="small text-uppercase fw-bold text-muted text-end pe-4">Apri</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        <i className="fas fa-inbox d-block fs-1 mb-2"></i>
                        Nessuna email trovata
                      </td>
                    </tr>
                  ) : rows.map((log) => {
                    const meta = typeMeta(log.emailType);
                    const smeta = sendStatusMeta(log.status);
                    const isError = smeta.color === 'danger';
                    return (
                      <tr key={log.id} className={isError ? 'table-danger' : ''}>
                        <td className="ps-4 text-nowrap text-muted small">{fmtDateTime(log.sentAt)}</td>
                        <td className="text-muted small">{log.to || '—'}</td>
                        <td className="text-muted small text-nowrap" title={`Evento #${log.eventId} del ${fmtDate(log.eventDate)}`}>
                          {log.eventName
                            ? <span className="badge bg-light text-muted border rounded-pill px-2 py-1" style={{ fontSize: 11 }}><i className="fas fa-calendar-alt me-1"></i>{log.eventName} <span className="opacity-75">({fmtDate(log.eventDate)} #{log.eventId})</span></span>
                            : '—'}
                        </td>
                        <td className="text-muted small" style={{ maxWidth: 260 }}>
                          <div className="text-truncate" title={log.subject}>{log.subject || '—'}</div>
                        </td>
                        <td>
                          <span className="badge rounded-pill px-3 py-1" style={{ backgroundColor: meta.color, color: '#fff' }}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="text-nowrap text-center">
                          <span className={`badge bg-${smeta.color} bg-opacity-10 text-${smeta.color} border rounded-pill px-3 py-1`}
                            title={isError ? `Non consegnabile: ${log.error || 'errore SMTP'}` : `Stato invio: ${smeta.label}`}>
                            <i className={`fas ${smeta.icon} me-1`}></i>{smeta.label}
                          </span>
                          {deliveryMeta(log.deliveryStatus) && (
                            <span className={`badge bg-${deliveryMeta(log.deliveryStatus).color} bg-opacity-10 text-${deliveryMeta(log.deliveryStatus).color} border rounded-pill px-2 py-1 ms-1`}
                              title={`Esito consegna al server di destinazione: ${deliveryMeta(log.deliveryStatus).label}${log.deliveryDetail ? ` — ${log.deliveryDetail}` : ''}`}>
                              <i className={`fas ${deliveryMeta(log.deliveryStatus).icon} me-1`}></i>{deliveryMeta(log.deliveryStatus).label}
                            </span>
                          )}
                        </td>
                        <td className="text-nowrap">
                          {log.readDate ? (
                            <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1">
                              <i className="fas fa-check-circle me-1"></i>Letta {fmtDateTime(log.readDate)}
                            </span>
                          ) : (
                            <span className="badge bg-light text-muted border rounded-pill px-3 py-1">
                              <i className="fas fa-envelope me-1"></i>Non letta
                            </span>
                          )}
                        </td>
                        <td className="text-muted small text-nowrap">{log.ipAddress || '—'}</td>
                        <td className="text-end pe-4">
                          <button className="btn btn-sm btn-light border rounded-circle" style={{ width: 30, height: 30 }} title="Apri email"
                            onClick={() => openDetail(log)}>
                            <i className="fas fa-eye" style={{ fontSize: 11 }}></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {total > LIMIT && (
          <div className="card-footer bg-transparent border-0 d-flex justify-content-between align-items-center py-2 px-3">
            <div className="text-muted small">
              {total === 0 ? 'Nessun risultato' : `${fromIdx}–${toIdx} di ${total}`}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" disabled={page <= 0} onClick={() => reload(page - 1)}>
                <i className="fas fa-chevron-left me-1"></i>Precedente
              </button>
              <span className="text-muted small">Pag. {page + 1} / {totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" disabled={page + 1 >= totalPages} onClick={() => reload(page + 1)}>
                Successiva<i className="fas fa-chevron-right ms-1"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {detail && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pt-3 px-4">
                <div className="modal-title fw-bold">
                  <i className="fas fa-envelope-open-text text-primary me-2"></i>Anteprima Email
                  <span className="badge ms-2" style={{ backgroundColor: typeMeta(detail.emailType).color, color: '#fff' }}>
                    {typeMeta(detail.emailType).label}
                  </span>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetail(null)}></button>
              </div>
              <div className="modal-body">
                <div className="bg-light rounded-3 px-3 py-2 small mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    <span className="text-muted"><i className="fas fa-user me-1"></i><strong>A:</strong> {detail.to || '—'}</span>
                    <span className="text-muted"><i className="fas fa-tag me-1"></i><strong>Oggetto:</strong> {detail.subject || '—'}</span>
                    <span className="text-muted"><i className="fas fa-clock me-1"></i><strong>Inviata:</strong> {fmtDateTime(detail.sentAt)}</span>
                    {(() => { const sm = sendStatusMeta(detail.status); return (
                      <span className={`text-${sm.color}`}><i className={`fas ${sm.icon} me-1`}></i><strong>Invio:</strong> {sm.label}</span>
                    ); })()}
                    {detail.readDate && (
                      <span className="text-success"><i className="fas fa-check-circle me-1"></i><strong>Letta:</strong> {fmtDateTime(detail.readDate)}{detail.ipAddress ? ` (IP ${detail.ipAddress})` : ''}</span>
                    )}
                    {detail.eventDate && (
                      <span className="text-muted"><i className="fas fa-calendar-alt me-1"></i><strong>Data evento:</strong> {detail.eventDate}</span>
                    )}
                  </div>
                  {detail.status === 'error' && detail.error && (
                    <div className="alert alert-danger py-2 small mb-2 mt-2">
                      <i className="fas fa-exclamation-triangle me-1"></i><strong>Non consegnabile:</strong> {detail.error}
                    </div>
                  )}
                </div>
                {detailError ? (
                  <div className="alert alert-danger py-2 small">
                    <i className="fas fa-exclamation-triangle me-1"></i>{detailError}
                  </div>
                ) : detailLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                  </div>
                ) : (
                  <iframe
                    title="Anteprima email"
                    sandbox="allow-popups"
                    style={{ width: '100%', height: '55vh', border: '1px solid #dee2e6', borderRadius: '8px', background: '#fff' }}
                    srcDoc={`<!DOCTYPE html><html><head><base target="_blank"></head><body>${stripTrackingPixels(detail.html)}</body></html>`}
                  />
                )}
              </div>
              <div className="modal-footer border-0">
                <div className="text-muted small"><i className="fas fa-lock me-1"></i>Consultazione in sola lettura</div>
                <button className="btn btn-primary rounded-pill px-4" onClick={() => setDetail(null)}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}